'use client';

import { c as _c } from "react/compiler-runtime";
import { type OptionFieldsFragment, type QuestionFieldsFragment, type VotingOption, VotingQuestionState } from 'api-graphql-types';
import { decodeColor, delay, range } from 'betterplace-utils';
import classNames from 'classnames';
import { Button, Confetti } from 'design-system/client';
import { Heading, LayoutCenter, LayoutStack } from 'design-system/server';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { type CSSProperties, type ReactNode, type RefObject, Suspense, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { composeSelectedFontObject } from '../helpers';
import type { VotingProps } from './types';
import useGoogleFonts from '@/fonts/useGoogleFonts';
import type { Maybe } from '@/types';
const VotingOptionsList = dynamic(() => import('./VotingOptionsList'), {
  ssr: false
});
const defaultDemoData = {
  id: '564654',
  options: [{
    id: '1',
    score: 999,
    text: 'Antwort 1'
  }, {
    id: '2',
    score: 555,
    text: 'Antwort 2'
  }, {
    id: '3',
    score: 111,
    text: 'Antwort 3'
  }] as OptionFieldsFragment[],
  aasmState: VotingQuestionState.Active,
  text: 'Beispiel-Abstimmung: Gegen wen soll ich spielen?'
} as QuestionFieldsFragment;
function requestAnimationFramePromise() {
  return new Promise(resolve => {
    requestAnimationFrame(timestamp => resolve(timestamp));
  });
}
async function animateWinScreen({
  trophy,
  trophyDoppelgangner: trophy2,
  triggerExplosion,
  done
}: {
  trophy: HTMLElement;
  trophyDoppelgangner: HTMLElement;
  triggerExplosion: () => void;
  done: () => void;
}) {
  await requestAnimationFramePromise();
  let trophy2Pos = trophy2.getBoundingClientRect();
  const organicTrophy2Height = trophy2Pos.height;
  let scale = 100 / organicTrophy2Height;
  trophy2.style.visibility = 'visible';
  trophy2.style.zIndex = '100000';
  trophy2.style.overflow = 'visible';
  trophy2.style.isolation = 'isolate';
  trophy2.style.transform = 'translateZ(0)'; // Forces GPU acceleration and helps with stacking context
  trophy2.style.opacity = '0';
  trophy2.style.transition = '';
  trophy2.style.transform = `scale(${scale})`;
  await requestAnimationFramePromise();
  trophy2.style.transition = 'transform 1500ms ease, opacity 300ms ease-out';
  trophy2.style.opacity = '1';
  for (let i = 0; i < 5; i++) {
    triggerExplosion();
    await delay(1500);
  }
  await requestAnimationFramePromise();
  trophy2Pos = trophy2.getBoundingClientRect();
  const targetPos = trophy.getBoundingClientRect();
  const originX = trophy2Pos.left + (trophy2Pos.width - targetPos.width) / 2;
  const originY = trophy2Pos.top + (trophy2Pos.height - targetPos.height) / 2;
  const deltaX = targetPos.left - originX;
  const deltaY = targetPos.top - originY;
  scale = targetPos.height / organicTrophy2Height;
  trophy2.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scale})`;
  await delay(800);
  done();
  await delay(1000);
  await requestAnimationFramePromise();
  trophy.style.visibility = 'visible';
  trophy2.style.visibility = 'hidden';
}
function WinScreen(t0) {
  const $ = _c(61);
  const {
    state,
    containerRef,
    winner,
    listReady,
    darkMode,
    questionTitle,
    onComplete,
    questionId
  } = t0;
  const prevStateRef = useRef(null);
  const onCompleteEffectFn = useEffectEvent(onComplete);
  const trophyDoppelgangerRef = useRef(null);
  const [playWinAnimation, setPlayWinAnimation] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [showExplosions, setConfettiExplosions] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setConfettiExplosions(_temp2);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const triggerExplosion = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = index => {
      setConfettiExplosions(previous_0 => {
        const next_0 = [...previous_0];
        next_0[index] = false;
        return next_0;
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const onExplosionEnd = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      prevStateRef.current = null;
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== questionId) {
    t5 = [questionId];
    $[4] = questionId;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== state) {
    t6 = () => {
      const prevState = prevStateRef.current;
      if (prevState === state) {
        return;
      }
      prevStateRef.current = state ?? null;
      if (!prevState) {
        return;
      }
      if (state === VotingQuestionState.Active) {
        return;
      }
      setPlayWinAnimation(true);
      setShowOverlay(true);
    };
    t7 = [state];
    $[6] = state;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] !== containerRef) {
    t8 = () => {
      if (!containerRef.current) {
        return null;
      }
      return containerRef.current.querySelector("table .trophy");
    };
    $[9] = containerRef;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const getWinningTrophyElement = t8;
  let t10;
  let t9;
  if ($[11] !== containerRef || $[12] !== getWinningTrophyElement || $[13] !== listReady || $[14] !== playWinAnimation || $[15] !== state) {
    t9 = () => {
      if (!containerRef.current) {
        return;
      }
      if (!listReady) {
        return;
      }
      const prevState_0 = prevStateRef.current;
      if (state !== VotingQuestionState.Finished) {
        return;
      }
      if (prevState_0 !== state) {
        return;
      }
      if (playWinAnimation) {
        return;
      }
      let frame = null;
      const trophy = getWinningTrophyElement();
      if (!trophy) {
        return;
      }
      frame = requestAnimationFrame(() => {
        trophy.style.visibility = "visible";
      });
      return () => {
        cancelAnimationFrame(frame);
      };
    };
    t10 = [containerRef, getWinningTrophyElement, listReady, playWinAnimation, state];
    $[11] = containerRef;
    $[12] = getWinningTrophyElement;
    $[13] = listReady;
    $[14] = playWinAnimation;
    $[15] = state;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  if ($[18] !== getWinningTrophyElement || $[19] !== listReady || $[20] !== onCompleteEffectFn || $[21] !== playWinAnimation) {
    t11 = () => {
      if (!listReady) {
        return;
      }
      const trophy_0 = getWinningTrophyElement();
      const trophyDoppelganger = trophyDoppelgangerRef.current;
      if (!trophy_0 || !trophyDoppelganger) {
        return;
      }
      if (!playWinAnimation) {
        return;
      }
      animateWinScreen({
        trophy: trophy_0,
        trophyDoppelgangner: trophyDoppelganger,
        triggerExplosion,
        done: () => setShowOverlay(false)
      }).then(() => {
        setPlayWinAnimation(false);
        onCompleteEffectFn();
      });
    };
    $[18] = getWinningTrophyElement;
    $[19] = listReady;
    $[20] = onCompleteEffectFn;
    $[21] = playWinAnimation;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== getWinningTrophyElement || $[24] !== listReady || $[25] !== playWinAnimation) {
    t12 = [getWinningTrophyElement, listReady, playWinAnimation, triggerExplosion];
    $[23] = getWinningTrophyElement;
    $[24] = listReady;
    $[25] = playWinAnimation;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  useEffect(t11, t12);
  const baseOverlayColor = darkMode ? "#000000" : "#ffffff";
  const t13 = showOverlay ? `${baseOverlayColor}a1` : `${baseOverlayColor}00`;
  const t14 = showOverlay ? "blur(10px)" : "blur(0)";
  let t15;
  if ($[27] !== t13 || $[28] !== t14) {
    t15 = {
      backgroundColor: t13,
      backdropFilter: t14
    };
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const t16 = showOverlay ? 1 : 0;
  let t17;
  if ($[30] !== t16) {
    t17 = {
      opacity: t16
    };
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = `var(${darkMode ? "--betterplace-color-fg-light-primary" : "--betterplace-color-fg-content-primary"})`;
  let t19;
  if ($[32] !== t18) {
    t19 = {
      fontSize: "2rem",
      color: t18
    };
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== questionTitle || $[35] !== t19) {
    t20 = <Heading level="text-md-important" as="h3" style={t19}>{questionTitle}</Heading>;
    $[34] = questionTitle;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      fontSize: "3rem"
    };
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  const t22 = winner?.text;
  let t23;
  if ($[38] !== t22) {
    t23 = <Heading level="heading-md" as="h2" className="votingHeadline" style={t21}>{t22}</Heading>;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t17 || $[41] !== t20 || $[42] !== t23) {
    t24 = <div className="winScreenHeadingsContainer" style={t17}>{t20}{t23}</div>;
    $[40] = t17;
    $[41] = t20;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== playWinAnimation || $[45] !== showExplosions) {
    t25 = playWinAnimation && [...range(2)].map(index_0 => {
      const showExplosion = showExplosions[index_0];
      return showExplosion && <Confetti key={index_0} onComplete={() => onExplosionEnd(index_0)} force={1.2} particleCount={200} duration={2500} particleSize={15} colors={["#FC440F", "#9ECB0A", "#7D83FF", "#F6CE46"]} width={window.screen.width + 200} />;
    });
    $[44] = playWinAnimation;
    $[45] = showExplosions;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t25) {
    t26 = <LayoutCenter centeredChildren={true} className="winScreenExplosionsContainer">{t25}</LayoutCenter>;
    $[47] = t25;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      visibility: "hidden"
    };
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== showOverlay) {
    t28 = classNames("trophy", "winScreenTrophy", {
      "glow": showOverlay
    });
    $[50] = showOverlay;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== t28) {
    t29 = <div className="trophyContainer"> <span ref={trophyDoppelgangerRef} style={t27} className={t28}>🏆</span></div>;
    $[52] = t28;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== t24 || $[55] !== t26 || $[56] !== t29) {
    t30 = <LayoutCenter centeredChildren={true} className="winScreenInnerContainer">{t24}{t26}{t29}</LayoutCenter>;
    $[54] = t24;
    $[55] = t26;
    $[56] = t29;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== t15 || $[59] !== t30) {
    t31 = <LayoutCenter centeredChildren={true} className="winScreen" style={t15}>{t30}</LayoutCenter>;
    $[58] = t15;
    $[59] = t30;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  return t31;
}
function _temp2(previous) {
  const next = [...previous];
  const falseIndex = next.findIndex(_temp);
  if (falseIndex >= 0) {
    next[falseIndex] = true;
    return next;
  }
  next.push(true);
  return next;
}
function _temp(ele) {
  return ele === false;
}
function Voting(t0) {
  const $ = _c(73);
  let className;
  let headlineFont;
  let headlineFontColor;
  let headlineFontSize;
  let headlineFontWeight;
  let props;
  let question;
  let withDemoData;
  if ($[0] !== t0) {
    const {
      withDemoData: t1,
      demoData,
      question: t2,
      headlineFont: t3,
      headlineFontColor: t4,
      headlineFontSize: t5,
      headlineFontWeight: t6,
      className: t7,
      ...t8
    } = t0;
    withDemoData = t1;
    question = t2;
    headlineFont = t3;
    headlineFontColor = t4;
    headlineFontSize = t5;
    headlineFontWeight = t6;
    className = t7;
    props = t8;
    $[0] = t0;
    $[1] = className;
    $[2] = headlineFont;
    $[3] = headlineFontColor;
    $[4] = headlineFontSize;
    $[5] = headlineFontWeight;
    $[6] = props;
    $[7] = question;
    $[8] = withDemoData;
  } else {
    className = $[1];
    headlineFont = $[2];
    headlineFontColor = $[3];
    headlineFontSize = $[4];
    headlineFontWeight = $[5];
    props = $[6];
    question = $[7];
    withDemoData = $[8];
  }
  const t = useTranslations("nextjs");
  const [demoQuestionState, setDemoQuestionState] = useState(VotingQuestionState.Active);
  const data = withDemoData ? {
    ...defaultDemoData,
    aasmState: demoQuestionState
  } : question;
  const [listReady, setListReady] = useState(false);
  const ref = useRef(null);
  const {
    dataFont,
    dataFontWeight,
    optionFont,
    optionFontWeight
  } = props;
  let t1;
  if ($[9] !== headlineFont || $[10] !== headlineFontWeight) {
    t1 = composeSelectedFontObject(headlineFont, headlineFontWeight);
    $[9] = headlineFont;
    $[10] = headlineFontWeight;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== optionFont || $[13] !== optionFontWeight) {
    t2 = composeSelectedFontObject(optionFont, optionFontWeight);
    $[12] = optionFont;
    $[13] = optionFontWeight;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== dataFont || $[16] !== dataFontWeight) {
    t3 = composeSelectedFontObject(dataFont, dataFontWeight);
    $[15] = dataFont;
    $[16] = dataFontWeight;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t3) {
    t4 = [t1, t2, t3];
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  const fonts = t4;
  let t5;
  if ($[22] !== fonts) {
    t5 = {
      fonts
    };
    $[22] = fonts;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  useGoogleFonts(t5);
  if (!data) {
    let t6;
    if ($[24] !== headlineFontColor) {
      t6 = decodeColor(headlineFontColor);
      $[24] = headlineFontColor;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    let t7;
    if ($[26] !== t6) {
      t7 = {
        color: t6
      };
      $[26] = t6;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    let t8;
    if ($[28] !== t) {
      t8 = t("streambot.live_widgets.voting.no_polls_yet");
      $[28] = t;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    let t9;
    if ($[30] !== t7 || $[31] !== t8) {
      t9 = <div style={t7}>{t8}</div>;
      $[30] = t7;
      $[31] = t8;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    return t9;
  }
  const options = data.options.slice().sort(_temp3);
  const totalAmount = options.reduce(_temp4, 0);
  const winner = options[0];
  let t6;
  if ($[33] !== className) {
    t6 = classNames("wrapper", className);
    $[33] = className;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      minInlineSize: "fit-content"
    };
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  let t8;
  if ($[36] !== headlineFontColor) {
    t8 = decodeColor(headlineFontColor);
    $[36] = headlineFontColor;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  const t9 = `${headlineFontSize}px`;
  let t10;
  if ($[38] !== headlineFont || $[39] !== headlineFontWeight || $[40] !== t8 || $[41] !== t9) {
    t10 = {
      "--streambot-font": headlineFont,
      "--streambot-font-color": t8,
      "--streambot-font-size": t9,
      "--streambot-font-weight": headlineFontWeight
    };
    $[38] = headlineFont;
    $[39] = headlineFontWeight;
    $[40] = t8;
    $[41] = t9;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  const t11 = t10 as CSSProperties;
  let t12;
  if ($[43] !== data.text) {
    t12 = <div className="votingHeadline">{data.text}</div>;
    $[43] = data.text;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== setListReady) {
    t13 = () => setListReady(true);
    $[45] = setListReady;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== options || $[48] !== props || $[49] !== t13 || $[50] !== totalAmount) {
    t14 = <table className="votingTable"><Suspense><VotingOptionsList {...props} options={options} totalAmount={totalAmount} onReady={t13} /></Suspense></table>;
    $[47] = options;
    $[48] = props;
    $[49] = t13;
    $[50] = totalAmount;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setDemoQuestionState(VotingQuestionState.Active);
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== data.aasmState || $[54] !== data.id || $[55] !== data.text || $[56] !== listReady || $[57] !== props.darkMode || $[58] !== winner) {
    t16 = <WinScreen containerRef={ref} state={data.aasmState} winner={winner} listReady={listReady} darkMode={props.darkMode} questionTitle={data.text} onComplete={t15} questionId={data.id} />;
    $[53] = data.aasmState;
    $[54] = data.id;
    $[55] = data.text;
    $[56] = listReady;
    $[57] = props.darkMode;
    $[58] = winner;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== t11 || $[61] !== t12 || $[62] !== t14 || $[63] !== t16) {
    t17 = <LayoutStack space="400" className="votingWrapper" style={t11}>{t12}{t14}{t16}</LayoutStack>;
    $[60] = t11;
    $[61] = t12;
    $[62] = t14;
    $[63] = t16;
    $[64] = t17;
  } else {
    t17 = $[64];
  }
  let t18;
  if ($[65] !== demoQuestionState || $[66] !== t || $[67] !== withDemoData) {
    t18 = withDemoData && <Button type="button" kind="secondary" size="small" onClick={() => setDemoQuestionState(VotingQuestionState.Finished)} className="votingTriggerWinButton" disabled={demoQuestionState === VotingQuestionState.Finished}>{demoQuestionState === VotingQuestionState.Active ? t("streambot.live_widgets.voting.trigger_win_animation") : t("core.loading")}</Button>;
    $[65] = demoQuestionState;
    $[66] = t;
    $[67] = withDemoData;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== t17 || $[70] !== t18 || $[71] !== t6) {
    t19 = <div className={t6} style={t7} ref={ref}>{t17}{t18}</div>;
    $[69] = t17;
    $[70] = t18;
    $[71] = t6;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  return t19;
}
function _temp4(acc, curr) {
  return acc + curr.score;
}
function _temp3(t0, t1) {
  const {
    score: a
  } = t0;
  const {
    score: b
  } = t1;
  return b - a;
}
export default Voting;